/**
 * NSLP (Non-Serviced Listing Page) ID Helper Utilities
 * 
 * NSLPs and Venues can have overlapping numeric IDs. To distinguish them
 * when they appear together in the UI (grid, map, etc.), we use a string prefix.
 */

const NSLP_PREFIX = 'nslp_';

// In-memory cache for NSLP venue IDs to avoid repeated API calls
const nslpVenueIdsCache = new Map();

/**
 * Check if an ID represents an NSLP (has the prefix)
 * @param {string|number} id - The ID to check
 * @returns {boolean} True if the ID is an NSLP ID
 */
export function isNslpId(id) {
  return id.toString().startsWith(NSLP_PREFIX);
}

/**
 * Extract numeric ID from a prefixed NSLP ID
 * @param {string} prefixedId - The prefixed NSLP ID (e.g., "nslp_3")
 * @returns {number} The numeric ID (e.g., 3)
 */
export function extractNslpId(prefixedId) {
  return parseInt(prefixedId.toString().replace(NSLP_PREFIX, ''), 10);
}

/**
 * Create a prefixed NSLP ID from a numeric ID
 * @param {number} numericId - The numeric ID (e.g., 3)
 * @returns {string} The prefixed NSLP ID (e.g., "nslp_3")
 */
export function makeNslpId(numericId) {
  return `${NSLP_PREFIX}${numericId}`;
}

/**
 * Get venue IDs from data attribute on an element
 * @param {string} nslpId - The NSLP ID
 * @returns {string[]|null} Array of venue ID strings or null if not found
 */
function getVenueIdsFromDataAttribute(nslpId) {
  // Try to find element with this venue ID that also has the shortlisting data
  // Using both selectors ensures we don't match map popups or sidebar cards that lack this attribute
  const element = document.querySelector(`[data-venue-id="${nslpId}"][data-venue-ids-for-shortlisting]`);
  
  if (element && element.dataset.venueIdsForShortlisting) {
    try {
      const venueIds = JSON.parse(element.dataset.venueIdsForShortlisting);
      if (Array.isArray(venueIds)) {
        return venueIds.map(id => id.toString());
      }
    } catch (error) {
      console.error('Error parsing venue IDs from data attribute:', error);
    }
  }
  
  return null;
}

/**
 * Fetch venue IDs associated with an NSLP
 * This function implements a three-tier lookup strategy:
 * 1. Check data attribute on the element (fastest, no API call)
 * 2. Check in-memory cache (fast, no API call)
 * 3. Fetch from API and cache the result (slower, makes API call)
 * 
 * @param {string} nslpId - The NSLP ID (with or without prefix)
 * @returns {Promise<string[]>} Array of venue ID strings
 */
export async function fetchNslpVenueIds(nslpId) {
  // 1. Check data attribute first (best performance)
  const dataAttributeIds = getVenueIdsFromDataAttribute(nslpId);
  if (dataAttributeIds) {
    // Cache it for future use
    nslpVenueIdsCache.set(nslpId, dataAttributeIds);
    return dataAttributeIds;
  }
  
  // 2. Check in-memory cache
  if (nslpVenueIdsCache.has(nslpId)) {
    return nslpVenueIdsCache.get(nslpId);
  }
  
  // 3. Fetch from API as last resort
  try {
    const response = await fetch(`/nslp_venue_ids?nslp_id=${nslpId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.venue_ids && Array.isArray(data.venue_ids)) {
      const venueIds = data.venue_ids.map(id => id.toString());
      // Cache the result
      nslpVenueIdsCache.set(nslpId, venueIds);
      return venueIds;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching NSLP venue IDs:', error);
    return [];
  }
}

/**
 * Clear the in-memory cache (useful for testing or after data updates)
 */
export function clearNslpCache() {
  nslpVenueIdsCache.clear();
}

